import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "styled-components";
import { toast } from "react-toastify";
import { Container } from "./styles";
import EmptyState from "../../components/EmptyState";
import api from "../../services/api";
import NFeDetailView from "./NFeDetailView";
import CTeDetailView from "./CTeDetailView";
import TransactionsLoadingSkeleton from "./TransactionsLoadingSkeleton";
import {
  MdSearch,
  MdFilterList,
  MdFileDownload,
  MdClose,
  MdChevronLeft,
  MdChevronRight,
  MdTrendingUp,
  MdTrendingDown,
} from "react-icons/md";
import {
  FiDownload,
  FiFilter,
  FiX,
  FiCalendar,
  FiHash,
  FiKey,
  FiCheckCircle,
  FiUser,
  FiFileText,
  FiAlertCircle,
  FiXCircle,
  FiActivity,
  FiLoader,
  FiCheckSquare,
  FiSquare,
  FiChevronUp,
  FiChevronDown,
  FiShield,
  FiLock,
} from "react-icons/fi";
import {
  HiOutlineDocumentText,
  HiOutlineIdentification,
  HiOutlineSwitchHorizontal,
  HiOutlineOfficeBuilding,
  HiOutlineUserGroup,
  HiOutlineXCircle,
} from "react-icons/hi";
import { BiSpreadsheet, BiBuildings } from "react-icons/bi";
import { BsFileEarmarkCode, BsCheckCircle, BsXCircle } from "react-icons/bs";
import { AiOutlineCloudDownload } from "react-icons/ai";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";

// Funções auxiliares
const formatNumber = (num) => {
  return num.toLocaleString("pt-BR");
};

const formatarCNPJ = (cnpj) =>
  cnpj
    ? cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
    : "N/A";

export default function Transactions() {
  const theme = useTheme();
  const { user } = useAuth();
  // Tab ativa persistida
  const [activeTab, setActiveTab] = usePersistedState(
    "vidal_transactions_tab",
    "relatorios"
  );
  const [filtroTexto, setFiltroTexto] = useState("");
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(false);

  // Estados para dados reais da API
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  });
  const [kpis, setKpis] = useState({
    nfe: 0,
    cte: 0,
    propria: 0,
    terceiros: 0,
    canceladas: 0,
  });
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({ field: "data", order: "desc" });
  const [mesAno, setMesAno] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  // Estados para modo de seleção múltipla
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

  // Estados para ativação SEFAZ
  const [showSefazModal, setShowSefazModal] = useState(false);
  const [sefazPassword, setSefazPassword] = useState('');
  const [activatingSefaz, setActivatingSefaz] = useState(false);
  const [sefazStatus, setSefazStatus] = useState({ loading: true, captureEnabled: false, hasCertificates: false, canSelfActivate: false });

  // Busca textual agora é feita no backend
  // O filtro local foi removido para fazer busca global no Elasticsearch
  const filteredMovimentacoes = movimentacoes;

  // Calcular KPIs filtrados
  const filteredKpis = useMemo(() => {
    const nfe = filteredMovimentacoes.filter((m) => m.modelo === "55").length;
    const cte = filteredMovimentacoes.filter((m) => m.modelo === "57").length;
    const canceladas = filteredMovimentacoes.filter(
      (m) => m.status !== "Autorizada"
    ).length;

    return {
      nfe,
      cte,
      propria: kpis.propria,
      terceiros: kpis.terceiros,
      canceladas,
    };
  }, [filteredMovimentacoes, kpis]);

  // Ref para debounce da busca
  const searchDebounceRef = useRef(null);
  // Ref para cancelar requisições pendentes e evitar resultados atrasados
  const abortControllerRef = useRef(null);
  // Ref para garantir que só a carga inicial exiba o skeleton
  const isFirstLoadRef = useRef(true);

  // Estados dos filtros
  const [filtros, setFiltros] = useState({
    dataInicio: "",
    dataFim: "",
    tipoDocumento: "todos",
    status: "todos",
    emissao: "todos",
    cnpj: "",
  });

  // Estados para autocomplete de emitentes
  const [emitenteSuggestions, setEmitenteSuggestions] = useState([]);
  const [showEmitenteSuggestions, setShowEmitenteSuggestions] = useState(false);
  const [loadingEmitentes, setLoadingEmitentes] = useState(false);
  const [emitenteActiveField, setEmitenteActiveField] = useState(null); // 'nome' ou 'cnpj'

  // Estados para autocomplete de destinatários
  const [destinatarioSuggestions, setDestinatarioSuggestions] = useState([]);
  const [showDestinatarioSuggestions, setShowDestinatarioSuggestions] = useState(false);
  const [loadingDestinatarios, setLoadingDestinatarios] = useState(false);
  const [destinatarioActiveField, setDestinatarioActiveField] = useState(null); // 'nome' ou 'cnpj'

  // Estados para autocomplete de CFOP
  const [cfopSuggestions, setCfopSuggestions] = useState([]);
  const [showCfopSuggestions, setShowCfopSuggestions] = useState(false);
  const [loadingCfops, setLoadingCfops] = useState(false);

  // Estados para filtro de empresas (certificados)
  const [empresas, setEmpresas] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [selectedEmpresas, setSelectedEmpresas] = useState([]);

  // Função para buscar KPIs da API
  const fetchKpis = async () => {
    try {
      const response = await api.get("/movimentacoes/kpis");

      if (response.data.success) {
        setKpis(response.data.kpis);
      }
    } catch (error) {
      console.error("Erro ao buscar KPIs:", error);
    }
  };

  // Função para buscar movimentações da API
  const fetchMovimentacoes = async (offset = 0, limit = 50, append = false, customFilters = null, searchQuery = null) => {
    // Cancela requisição anterior para evitar trocas de resultados
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (append) {
      setLoadingMore(true);
    } else if (isFirstLoadRef.current) {
      // Primeiro carregamento mantém o skeleton
      setLoading(true);
    } else {
      // Buscas e filtros subsequentes não piscam a tela
      setRefreshing(true);
    }

    try {
      // Usar filtros customizados se fornecidos, senão usar o estado atual
      const activeFilters = customFilters || filtros;
      
      // Preparar parâmetros de filtro
      const params = {
        limit: limit,
        offset: offset,
      };

      // Adicionar filtros se não forem "todos"
      if (activeFilters.tipoDocumento && activeFilters.tipoDocumento !== "todos") {
        params.tipoDocumento = activeFilters.tipoDocumento;
      }
      if (activeFilters.status && activeFilters.status !== "todos") {
        params.status = activeFilters.status;
      }
      if (activeFilters.emissao && activeFilters.emissao !== "todos") {
        params.emissao = activeFilters.emissao;
      }
      if (activeFilters.cfop) {
        params.cfop = activeFilters.cfop;
      }
      if (activeFilters.emitenteNome) {
        params.emitenteNome = activeFilters.emitenteNome;
      }
      if (activeFilters.emitenteCnpj) {
        params.emitenteCnpj = activeFilters.emitenteCnpj;
      }
      if (activeFilters.destinatarioNome) {
        params.destinatarioNome = activeFilters.destinatarioNome;
      }
      if (activeFilters.destinatarioCnpj) {
        params.destinatarioCnpj = activeFilters.destinatarioCnpj;
      }
      if (activeFilters.dataInicio) {
        params.dataInicio = activeFilters.dataInicio;
      }
      if (activeFilters.dataFim) {
        params.dataFim = activeFilters.dataFim;
      }

      // Adicionar filtro de empresas selecionadas
      if (selectedEmpresas.length > 0) {
        params.empresas = selectedEmpresas.join(',');
        console.log("🏢 Filtro de empresas aplicado:", params.empresas);
      }

      // Adicionar busca textual se fornecida
      const currentSearch = searchQuery !== null ? searchQuery : searchText;
      if (currentSearch && currentSearch.trim()) {
        params.search = currentSearch.trim();
      }

      // Adicionar ordenação
      if (sortConfig.field) {
        params.sortField = sortConfig.field;
        params.sortOrder = sortConfig.order;
      }

      console.log("📤 Parâmetros enviados para API:", params);

      const response = await api.get("/movimentacoes/lista", {
        params: params,
        signal: controller.signal,
      });

      console.log("📥 Resposta da API - Total de documentos:", response.data?.pagination?.total);

      if (response.data.success) {
        // Fetching movimentacoes
        if (append) {
          setMovimentacoes((prev) => [...prev, ...response.data.movimentacoes]);
        } else {
          setMovimentacoes(response.data.movimentacoes);
        }
        setPagination(response.data.pagination);
      }
    } catch (error) {
      if (error.name === "AbortError" || error.code === "ERR_CANCELED") {
        // Requisição cancelada por uma busca mais recente — ignorar
      } else {
        console.error("❌ Erro ao buscar movimentações:", error);
      }
    } finally {
      // Só limpa o estado se esta ainda for a requisição ativa
      if (abortControllerRef.current !== controller) return;
      if (append) {
        setLoadingMore(false);
      } else if (isFirstLoadRef.current) {
        setLoading(false);
        isFirstLoadRef.current = false;
      } else {
        setRefreshing(false);
      }
    }
  };

  // Função para lidar com a busca textual com debounce
  const handleSearchTextChange = (value) => {
    setSearchText(value);
    
    // Limpar timeout anterior
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Criar novo timeout para buscar após 300ms de inatividade
    searchDebounceRef.current = setTimeout(() => {
      fetchMovimentacoes(0, 50, false, null, value);
    }, 300);
  };

  // Ref para o observer do infinite scroll
  const observerTarget = useRef(null);

  // Função para carregar mais movimentações
  const loadMore = useCallback(() => {
    if (!loadingMore && !loading && !refreshing && pagination.hasMore) {
      const newOffset = pagination.offset + pagination.limit;
      fetchMovimentacoes(newOffset, pagination.limit, true);
    }
  }, [loadingMore, loading, refreshing, pagination]);

  // Função para remover filtro e aplicar automaticamente
  const removeFilter = (filterKey, resetValue = "todos") => {
    const newFiltros = { ...filtros, [filterKey]: resetValue };
    setFiltros(newFiltros);
    // Passar os novos filtros diretamente para evitar problemas de estado
    fetchMovimentacoes(0, 50, false, newFiltros);
  };

  // Handler para ordenação por coluna
  const handleSort = (field) => {
    setSortConfig((prev) =>
      prev.field === field
        ? { field, order: prev.order === "desc" ? "asc" : "desc" }
        : { field, order: "desc" }
    );
  };

  // Helper para renderizar ícone de ordenação no header
  const renderSortIcon = (field) => {
    const isActive = sortConfig.field === field;
    if (isActive) {
      return sortConfig.order === "asc" ? (
        <FiChevronUp className="sort-icon active" />
      ) : (
        <FiChevronDown className="sort-icon active" />
      );
    }
    return <FiChevronDown className="sort-icon" />;
  };

  // Ref para debounce
  const searchTimeout = useRef(null);

  // Função para buscar emitentes (autocomplete) - usando API real
  const searchEmitentes = async (query) => {
    if (!query || query.length < 1) {
      setEmitenteSuggestions([]);
      setShowEmitenteSuggestions(false);
      return;
    }

    setLoadingEmitentes(true);
    
    try {
      const response = await api.get("/movimentacoes/search-emitentes", {
        params: { query }
      });

      if (response.data.success) {
        setEmitenteSuggestions(response.data.emitentes);
        setShowEmitenteSuggestions(response.data.emitentes.length > 0);
      }
    } catch (error) {
      console.error("Erro ao buscar emitentes:", error);
      setEmitenteSuggestions([]);
      setShowEmitenteSuggestions(false);
    } finally {
      setLoadingEmitentes(false);
    }
  };

  // Handler para mudança no input de emitente (com debounce)
  const handleEmitenteChange = (value) => {
    setFiltros({ ...filtros, emitenteNome: value });
    setEmitenteActiveField('nome');
    
    // Limpar timeout anterior
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Criar novo timeout
    searchTimeout.current = setTimeout(() => {
      searchEmitentes(value);
    }, 300);
  };

  // Handler para selecionar um emitente da lista
  const selectEmitente = (emitente) => {
    setFiltros({ 
      ...filtros, 
      emitenteNome: emitente.nome,
      emitenteCnpj: emitente.cnpj
    });
    setShowEmitenteSuggestions(false);
    setEmitenteActiveField(null);
  };

  // Função para buscar destinatários (autocomplete) - usando API real
  const searchDestinatarios = async (query) => {
    console.log("🔍 searchDestinatarios chamado com:", query);
    
    if (!query || query.length < 1) {
      setDestinatarioSuggestions([]);
      setShowDestinatarioSuggestions(false);
      return;
    }

    setLoadingDestinatarios(true);
    console.log("⏳ Buscando destinatários...");
    
    try {
      const response = await api.get("/movimentacoes/search-destinatarios", {
        params: { query }
      });

      console.log("✅ Resposta destinatários:", response.data);

      if (response.data.success) {
        setDestinatarioSuggestions(response.data.destinatarios);
        setShowDestinatarioSuggestions(response.data.destinatarios.length > 0);
        console.log("📋 Sugestões definidas:", response.data.destinatarios.length);
      }
    } catch (error) {
      console.error("❌ Erro ao buscar destinatários:", error);
      setDestinatarioSuggestions([]);
      setShowDestinatarioSuggestions(false);
    } finally {
      setLoadingDestinatarios(false);
    }
  };

  // Handler para mudança no input de destinatário (com debounce)
  const handleDestinatarioChange = (value) => {
    setFiltros({ ...filtros, destinatarioNome: value });
    setDestinatarioActiveField('nome');
    
    // Limpar timeout anterior
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Criar novo timeout
    searchTimeout.current = setTimeout(() => {
      searchDestinatarios(value);
    }, 300);
  };

  // Handler para selecionar um destinatário da lista
  const selectDestinatario = (destinatario) => {
    setFiltros({ 
      ...filtros, 
      destinatarioNome: destinatario.nome,
      destinatarioCnpj: destinatario.cnpj
    });
    setShowDestinatarioSuggestions(false);
    setDestinatarioActiveField(null);
  };

  // Handler para mudança no input de CNPJ do emitente (com debounce)
  const handleEmitenteCnpjChange = (value) => {
    setFiltros({ ...filtros, emitenteCnpj: value });
    setEmitenteActiveField('cnpj');
    
    // Limpar timeout anterior
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Criar novo timeout
    searchTimeout.current = setTimeout(() => {
      searchEmitentes(value);
    }, 300);
  };

  // Handler para mudança no input de CNPJ do destinatário (com debounce)
  const handleDestinatarioCnpjChange = (value) => {
    setFiltros({ ...filtros, destinatarioCnpj: value });
    setDestinatarioActiveField('cnpj');
    
    // Limpar timeout anterior
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Criar novo timeout
    searchTimeout.current = setTimeout(() => {
      searchDestinatarios(value);
    }, 300);
  };

  // Função para buscar CFOPs (autocomplete)
  const searchCfops = async (query) => {
    if (!query || query.length < 1) {
      setCfopSuggestions([]);
      setShowCfopSuggestions(false);
      return;
    }

    setLoadingCfops(true);
    
    try {
      const response = await api.get("/cfop/search", {
        params: { query }
      });

      if (response.data.success) {
        setCfopSuggestions(response.data.cfops);
        setShowCfopSuggestions(response.data.cfops.length > 0);
      }
    } catch (error) {
      console.error("Erro ao buscar CFOPs:", error);
      setCfopSuggestions([]);
      setShowCfopSuggestions(false);
    } finally {
      setLoadingCfops(false);
    }
  };

  // Handler para mudança no input de CFOP (com debounce)
  const handleCfopChange = (value) => {
    setFiltros({ ...filtros, cfop: value });
    
    // Limpar timeout anterior
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    // Criar novo timeout
    searchTimeout.current = setTimeout(() => {
      searchCfops(value);
    }, 300);
  };

  // Handler para selecionar um CFOP da lista
  const selectCfop = (cfop) => {
    setFiltros({ 
      ...filtros, 
      cfop: cfop.codigo
    });
    setShowCfopSuggestions(false);
  };

  // Função para fazer download do XML
  const handleDownloadXml = async (chave, e) => {
    e.stopPropagation();
    try {
      const response = await api.get(`/movimentacoes/download/${chave}`, {
        responseType: "blob",
      });

      // Criar URL do blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${chave}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar XML:", error);
      alert("Erro ao baixar XML. Tente novamente.");
    }
  };

  // Função para manipular seleção de itens (click, shift+click)
  const handleItemClick = (mov, index, event) => {
    if (!selectionMode) {
      // Se não está em modo seleção, abre detalhes normalmente
      handleViewDetails(mov);
      return;
    }

    const chave = mov.chave;
    const newSelected = new Set(selectedItems);

    if (event.shiftKey && lastSelectedIndex !== null) {
      // Shift+Click: Selecionar intervalo
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      
      for (let i = start; i <= end; i++) {
        if (filteredMovimentacoes[i]) {
          newSelected.add(filteredMovimentacoes[i].chave);
        }
      }
    } else if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd+Click: Limpa tudo e seleciona apenas este
      newSelected.clear();
      newSelected.add(chave);
    } else {
      // Click simples: Toggle individual (adiciona ou remove)
      if (newSelected.has(chave)) {
        newSelected.delete(chave);
      } else {
        newSelected.add(chave);
      }
    }

    setSelectedItems(newSelected);
    setLastSelectedIndex(index);
  };

  // Função para toggle do modo de seleção
  const toggleSelectionMode = () => {
    if (selectionMode) {
      // Desativar modo seleção - limpar seleção
      setSelectedItems(new Set());
      setLastSelectedIndex(null);
    }
    setSelectionMode(!selectionMode);
  };

  // Função para selecionar todos os itens visíveis
  const selectAllVisible = () => {
    const allChaves = new Set(filteredMovimentacoes.map(m => m.chave));
    setSelectedItems(allChaves);
  };

  // Função para desselecionar todos
  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  // Função para buscar detalhes completos do documento
  const handleViewDetails = async (mov) => {
    setLoadingDetails(true);
    try {
      // Buscar documento completo do Elasticsearch via backend
      const response = await api.get(`/movimentacoes/detalhes/${mov.id}`);

      if (response.data.success) {
        setSelectedDoc(response.data.documento);
        setSelectedDocType(mov.modelo === "57" ? "cte" : "nfe");
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      alert("Erro ao carregar detalhes do documento.");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Função para buscar certificados (empresas) do projeto
  const fetchEmpresas = async () => {
    console.log("🔍 fetchEmpresas() foi chamada");
    setLoadingEmpresas(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      console.log("📁 Project ID do localStorage:", projectId);
      
      if (!projectId) {
        console.warn("⚠️ Nenhum projeto selecionado");
        setEmpresas([]);
        setLoadingEmpresas(false);
        return;
      }

      console.log("🌐 Buscando certificados para projeto:", projectId);
      const response = await api.get(`/certificates/list/${projectId}`);
      
      console.log("✅ Resposta da API de certificados:", response.data);
      
      // A API retorna { success: true, data: [...], total: N }
      if (response.data?.success && Array.isArray(response.data.data)) {
        console.log("📋 Certificados encontrados:", response.data.data.length);
        // Mapear para o formato esperado pelo componente
        const empresasFormatadas = response.data.data.map(cert => ({
          cnpj: cert.cnpj,
          nome: cert.companyName.split(':')[0], // Remove o CNPJ do final do nome
          id: cert.id,
          validFrom: cert.validFrom,
          validTo: cert.validTo
        }));
        setEmpresas(empresasFormatadas);
      } else {
        console.warn("⚠️ Formato de resposta inesperado:", response.data);
        setEmpresas([]);
      }
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
      setEmpresas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  // Função para toggle seleção de empresa
  const toggleEmpresa = (cnpj) => {
    setSelectedEmpresas(prev => {
      if (prev.includes(cnpj)) {
        return prev.filter(c => c !== cnpj);
      } else {
        return [...prev, cnpj];
      }
    });
  };


  // Função para ativar captura SEFAZ
  const handleActivateSefaz = async () => {
    if (!sefazPassword) return;
    setActivatingSefaz(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      await api.post('/sefaz/activate-capture', { projectId });
      toast.success('Captura SEFAZ ativada! As notas serão baixadas em breve.');
      setSefazStatus(prev => ({ ...prev, captureEnabled: true }));
      setShowSefazModal(false);
      setSefazPassword('');
      // Buscar movimentações após ativação
      setTimeout(() => {
        fetchMovimentacoes();
        fetchKpis();
      }, 2000);
    } catch (error) {
      console.error('Erro ao ativar captura SEFAZ:', error);
      toast.error(error.response?.data?.error || 'Erro ao ativar captura SEFAZ');
    } finally {
      setActivatingSefaz(false);
    }
  };

  // Função para voltar da view de detalhes
  const handleCloseModal = () => {
    setSelectedDoc(null);
    setSelectedDocType(null);
  };

  // Função para buscar status da captura SEFAZ
  const fetchSefazStatus = async () => {
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      if (!projectId) return;
      console.log('[SefazStatus] Buscando status para projeto:', projectId);
      const response = await api.get(`/sefaz/capture-status/${projectId}`);
      if (response.data?.success) {
        console.log('[SefazStatus] Resposta:', response.data.data);
        setSefazStatus({
          loading: false,
          captureEnabled: response.data.data?.captureEnabled || false,
          hasCertificates: response.data.data?.hasCertificates || false,
          canSelfActivate: response.data.data?.canSelfActivate || false,
        });
      }
    } catch (error) {
      console.error('Erro ao buscar status SEFAZ:', error);
      setSefazStatus({ loading: false, captureEnabled: false, hasCertificates: false, canSelfActivate: false });
    }
  };

  // Buscar movimentações, KPIs, empresas e status SEFAZ ao montar o componente
  useEffect(() => {
    fetchMovimentacoes();
    fetchKpis();
    fetchEmpresas();
    fetchSefazStatus();
  }, []);


  // Re-buscar quando a ordenação mudar
  const sortMountRef = useRef(true);
  useEffect(() => {
    if (sortMountRef.current) {
      sortMountRef.current = false;
      return;
    }
    fetchMovimentacoes(0, 50, false);
  }, [sortConfig]);

  // Polling: buscar movimentações a cada 30s quando captura está ativada mas sem dados
  useEffect(() => {
    if (!sefazStatus.captureEnabled || movimentacoes.length > 0) return;
    const interval = setInterval(() => {
      fetchMovimentacoes();
      fetchKpis();
    }, 30000);
    return () => clearInterval(interval);
  }, [sefazStatus.captureEnabled, movimentacoes.length]);

  // Polling: buscar status SEFAZ a cada 60s para manter sincronizado
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSefazStatus();
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Nota: A lógica de reload ao trocar projeto foi movida para a StatusBar global

  // Infinite scroll com IntersectionObserver
  useEffect(() => {
    // Não ativar observer se não há mais dados ou está carregando
    if (!pagination.hasMore || loading || loadingMore || refreshing) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          pagination.hasMore &&
          !loading &&
          !loadingMore &&
          !refreshing
        ) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loadMore, pagination.hasMore, loading, loadingMore, refreshing]);

  // Dados mockados - cards de resumo
  const resumo = {
    totalXml: 3758,
    downloads: 3758,
    canceladas: 14,
    faltantes: 0,
  };

  // Dados mockados - Mensal por dia (com NFe, CTe, emissão própria e terceiros)
  const dadosMensal = {
    monthlyTotal: 3758,
    monthlyNfe: 2890,
    monthlyCte: 868,
    monthlyPropria: 1245,
    monthlyTerceiros: 2513,
    monthlyCanceladas: 14,
    reportsByDay: {
      1: { nfe: 95, cte: 30, propria: 42, terceiros: 83, canceladas: 2 },
      2: { nfe: 75, cte: 23, propria: 33, terceiros: 65, canceladas: 0 },
      3: { nfe: 120, cte: 36, propria: 52, terceiros: 104, canceladas: 1 },
      4: { nfe: 102, cte: 32, propria: 45, terceiros: 89, canceladas: 0 },
      5: { nfe: 145, cte: 44, propria: 63, terceiros: 126, canceladas: 2 },
      6: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      7: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      8: { nfe: 110, cte: 35, propria: 48, terceiros: 97, canceladas: 1 },
      9: { nfe: 128, cte: 39, propria: 56, terceiros: 111, canceladas: 0 },
      10: { nfe: 136, cte: 42, propria: 59, terceiros: 119, canceladas: 2 },
      11: { nfe: 109, cte: 34, propria: 48, terceiros: 95, canceladas: 0 },
      12: { nfe: 152, cte: 46, propria: 66, terceiros: 132, canceladas: 1 },
      13: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      14: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      15: { nfe: 119, cte: 37, propria: 52, terceiros: 104, canceladas: 0 },
      16: { nfe: 143, cte: 44, propria: 62, terceiros: 125, canceladas: 1 },
      17: { nfe: 126, cte: 39, propria: 55, terceiros: 110, canceladas: 0 },
      18: { nfe: 102, cte: 32, propria: 45, terceiros: 89, canceladas: 2 },
      19: { nfe: 136, cte: 42, propria: 59, terceiros: 119, canceladas: 0 },
      20: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      21: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      22: { nfe: 110, cte: 35, propria: 48, terceiros: 97, canceladas: 1 },
      23: { nfe: 145, cte: 44, propria: 63, terceiros: 126, canceladas: 0 },
      24: { nfe: 128, cte: 39, propria: 56, terceiros: 111, canceladas: 0 },
      25: { nfe: 119, cte: 37, propria: 52, terceiros: 104, canceladas: 1 },
      26: { nfe: 136, cte: 42, propria: 59, terceiros: 119, canceladas: 0 },
      27: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      28: { nfe: 0, cte: 0, propria: 0, terceiros: 0, canceladas: 0 },
      29: { nfe: 102, cte: 32, propria: 45, terceiros: 89, canceladas: 0 },
      30: { nfe: 119, cte: 37, propria: 52, terceiros: 104, canceladas: 0 },
    },
  };

  // Dados mensais para tabela de meses
  const dadosMeses = [
    {
      mes: "Jan/25",
      nfe: 2654,
      cte: 798,
      propria: 1150,
      terceiros: 2302,
      canceladas: 12,
    },
    {
      mes: "Fev/25",
      nfe: 2432,
      cte: 730,
      propria: 1054,
      terceiros: 2108,
      canceladas: 8,
    },
    {
      mes: "Mar/25",
      nfe: 2876,
      cte: 863,
      propria: 1246,
      terceiros: 2493,
      canceladas: 15,
    },
    {
      mes: "Abr/25",
      nfe: 2745,
      cte: 824,
      propria: 1190,
      terceiros: 2379,
      canceladas: 11,
    },
    {
      mes: "Mai/25",
      nfe: 2987,
      cte: 896,
      propria: 1294,
      terceiros: 2589,
      canceladas: 18,
    },
    {
      mes: "Jun/25",
      nfe: 2654,
      cte: 796,
      propria: 1150,
      terceiros: 2300,
      canceladas: 9,
    },
    {
      mes: "Jul/25",
      nfe: 2876,
      cte: 863,
      propria: 1246,
      terceiros: 2493,
      canceladas: 14,
    },
    {
      mes: "Ago/25",
      nfe: 3012,
      cte: 904,
      propria: 1305,
      terceiros: 2611,
      canceladas: 16,
    },
    {
      mes: "Set/25",
      nfe: 2789,
      cte: 837,
      propria: 1208,
      terceiros: 2418,
      canceladas: 10,
    },
    {
      mes: "Out/25",
      nfe: 2945,
      cte: 884,
      propria: 1276,
      terceiros: 2553,
      canceladas: 13,
    },
    {
      mes: "Nov/25",
      nfe: 2823,
      cte: 847,
      propria: 1223,
      terceiros: 2447,
      canceladas: 11,
    },
    {
      mes: "Dez/25",
      nfe: 2890,
      cte: 868,
      propria: 1245,
      terceiros: 2513,
      canceladas: 14,
    },
  ];

  // Dados para gráficos donut
  const chartData = {
    emissoes: [
      { name: "NFe", value: 2450, color: "#3b82f6" },
      { name: "CTe", value: 850, color: "#10b981" },
      { name: "NFSe", value: 458, color: "#f59e0b" },
    ],
    status: [
      {
        name: "Autorizadas",
        value: dadosMensal.monthlyBaixadas,
        color: "#10b981",
      },
      {
        name: "Canceladas",
        value: dadosMensal.monthlyCanceladas,
        color: "#ef4444",
      },
      {
        name: "Faltantes",
        value: dadosMensal.monthlyFaltantes,
        color: "#f59e0b",
      },
    ],
  };

  // Função para navegar entre meses
  const navegarMes = (direcao) => {
    const [ano, mes] = mesAno.split("-").map(Number);
    let novoMes = mes;
    let novoAno = ano;

    if (direcao === "anterior") {
      novoMes--;
      if (novoMes === 0) {
        novoMes = 12;
        novoAno--;
      }
    } else {
      novoMes++;
      if (novoMes === 13) {
        novoMes = 1;
        novoAno++;
      }
    }

    setMesAno(`${novoAno}-${novoMes.toString().padStart(2, "0")}`);
  };

  // Função para obter nome do mês
  const obterNomeMes = () => {
    const [ano, mes] = mesAno.split("-");
    const data = new Date(ano, mes - 1);
    return data
      .toLocaleDateString("pt-BR", { month: "long", year: "numeric" })
      .replace(/^\w/, (c) => c.toUpperCase());
  };

  // NFes do mês (mockado)
  const nfesMensal = [
    {
      id: 1,
      data: "01/12/2025",
      numero: "240001",
      chave: "35251200428307002315700100024000110024000111",
      status: "Autorizada",
      cnpj: "15363886000105",
      emitente: "EMPRESA ALPHA LTDA",
    },
    {
      id: 2,
      data: "03/12/2025",
      numero: "240002",
      chave: "35251229657559000170550010005975012686855966",
      status: "Autorizada",
      cnpj: "29657559000170",
      emitente: "BETA COMERCIO SA",
    },
    {
      id: 3,
      data: "05/12/2025",
      numero: "240003",
      chave: "35251202188572000171550010004350801543051884",
      status: "Cancelada",
      cnpj: "02188572000171",
      emitente: "GAMMA INDUSTRIA ME",
    },
    {
      id: 4,
      data: "08/12/2025",
      numero: "240004",
      chave: "31251216701716000156550250079236981376754718",
      status: "Autorizada",
      cnpj: "16701716000156",
      emitente: "DELTA TRANSPORTES LTDA",
    },
    {
      id: 5,
      data: "10/12/2025",
      numero: "240005",
      chave: "42251228487930001595700100871896614116054T",
      status: "Autorizada",
      cnpj: "03007331011186",
      emitente: "EPSILON SERVICOS EIRELI",
    },
  ];

  // Dados mockados - tabela de NFes (como na imagem do vidal)
  const nfes = [
    {
      id: 1,
      data: "05/12/2025",
      numero: "242994",
      chave: "35251200428307002315700100024299410024299944",
      status: "Autorizada",
      cnpj: "15363886000105",
      emitente: "BAURU - EXPRESSO SAO MIGUEL S/A",
    },
    {
      id: 2,
      data: "05/12/2025",
      numero: "59750",
      chave: "35251229657559000170550010005975012686855966",
      status: "Autorizada",
      cnpj: "29657559000170",
      emitente: "VALVERDE DISTRIBUIDORA DE VEICULOS LTDA",
    },
    {
      id: 3,
      data: "05/12/2025",
      numero: "435080",
      chave: "35251202188572000171550010004350801543051884",
      status: "Autorizada",
      cnpj: "02188572000171",
      emitente: "Quite Conveniencias Auto e Ind Ltda",
    },
    {
      id: 4,
      data: "05/12/2025",
      numero: "7923698",
      chave: "31251216701716000156550250079236981376754718",
      status: "Autorizada",
      cnpj: "16701716000156",
      emitente: "Stellantis Automoveis Brasil Ltda.",
    },
    {
      id: 5,
      data: "05/12/2025",
      numero: "8718966",
      chave: "42251228487930001595700100871896614116054T",
      status: "Autorizada",
      cnpj: "03007331011186",
      emitente: "ALTO VALE TRANSPORTES E LOGISTICA EIRELI",
    },
    {
      id: 6,
      data: "05/12/2025",
      numero: "591902",
      chave: "31251203349915000286570010005919021024620390",
      status: "Autorizada",
      cnpj: "16701716002957",
      emitente: "JAT TRANSPORTES E LOGISTICA S.A.",
    },
    {
      id: 7,
      data: "05/12/2025",
      numero: "591901",
      chave: "31251203349915000286570010005919011024620407",
      status: "Autorizada",
      cnpj: "16701716002957",
      emitente: "JAT TRANSPORTES E LOGISTICA S.A.",
    },
    {
      id: 8,
      data: "05/12/2025",
      numero: "591900",
      chave: "31251203349915000286570010005919001024620388",
      status: "Autorizada",
      cnpj: "16701716002957",
      emitente: "JAT TRANSPORTES E LOGISTICA S.A.",
    },
  ];

  // Mostrar skeleton durante loading
  if (loading) {
    return <TransactionsLoadingSkeleton isDark={theme.body !== "#fafbfc"} />;
  }

  // Mostrar detail view inline quando um documento é selecionado
  if (selectedDoc) {
    return (
      <Container>
        {selectedDocType === "cte" ? (
          <CTeDetailView doc={selectedDoc} onBack={handleCloseModal} />
        ) : (
          <NFeDetailView doc={selectedDoc} onBack={handleCloseModal} />
        )}
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-details">
          <h3>Dashboard de Movimentações</h3>
          <div className="header-meta">
            <span className="status-tag">SISTEMA ATIVO</span>
            <span className="update-tag">
              {movimentacoes.length > 0
                ? `${formatNumber(movimentacoes.length)} documento${movimentacoes.length !== 1 ? 's' : ''} carregado${movimentacoes.length !== 1 ? 's' : ''}`
                : 'nenhum documento'}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs - Apenas Relatórios */}
      <div className="tabs-header">
        <button
          className={`tab active`}
          onClick={() => setActiveTab("relatorios")}
        >
          <BiSpreadsheet />
          Relatórios
        </button>
      </div>

      {/* ========== TAB RELATÓRIOS ========== */}
      {activeTab === "relatorios" && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.8em",
            margin: 0,
            padding: 0,
          }}
        >
          {/* Cards de resumo - KPIs reais */}
          <div className="metric-cards" style={{ margin: 0, padding: 0 }}>
            <div className="metric-card blue">
              <div className="metric-icon blue">
                <HiOutlineDocumentText />
              </div>
              <div className="metric-content">
                <span className="metric-value">
                  {formatNumber(searchText ? filteredKpis.nfe : kpis.nfe)}
                </span>
                <span className="metric-label">NFe</span>
              </div>
            </div>
            <div className="metric-card green">
              <div className="metric-icon green">
                <HiOutlineDocumentText />
              </div>
              <div className="metric-content">
                <span className="metric-value">
                  {formatNumber(searchText ? filteredKpis.cte : kpis.cte)}
                </span>
                <span className="metric-label">CTe</span>
              </div>
            </div>
            <div className="metric-card purple">
              <div className="metric-icon purple">
                <HiOutlineOfficeBuilding />
              </div>
              <div className="metric-content">
                <span className="metric-value">
                  {formatNumber(kpis.propria)}
                </span>
                <span className="metric-label">Própria</span>
              </div>
            </div>
            <div className="metric-card orange">
              <div className="metric-icon orange">
                <HiOutlineUserGroup />
              </div>
              <div className="metric-content">
                <span className="metric-value">
                  {formatNumber(kpis.terceiros)}
                </span>
                <span className="metric-label">Terceiros</span>
              </div>
            </div>
            <div className="metric-card red">
              <div className="metric-icon red">
                <HiOutlineXCircle />
              </div>
              <div className="metric-content">
                <span className="metric-value">
                  {formatNumber(
                    searchText ? filteredKpis.canceladas : kpis.canceladas
                  )}
                </span>
                <span className="metric-label">Canceladas</span>
              </div>
            </div>
          </div>

          <div className="toolbar" style={{ margin: 0, marginBottom: "0.5em" }}>
            <div className="toolbar-left" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {selectionMode && selectedItems.size > 0 && (
                <button
                  className="toolbar-btn"
                  onClick={deselectAll}
                >
                  <FiX />
                  Limpar
                </button>
              )}
              <button
                className={`toolbar-btn ${selectionMode ? 'active' : ''}`}
                onClick={toggleSelectionMode}
              >
                {selectionMode ? <FiCheckSquare /> : <FiSquare />}
                {selectionMode ? `${selectedItems.size} selecionado${selectedItems.size !== 1 ? 's' : ''}` : 'Selecionar'}
              </button>
              {selectionMode && (
                <button
                  className="toolbar-btn"
                  onClick={selectAllVisible}
                >
                  <FiCheckSquare />
                  Selecionar Tudo
                </button>
              )}
              <button
                className="toolbar-btn"
                disabled={selectedItems.size === 0}
                onClick={() => {
                  const chaves = Array.from(selectedItems).join(',');
                  const projeto = localStorage.getItem('vidal_selected_project');
                  const token = JSON.parse(localStorage.getItem('vidal_auth') || '{}').token || '';
                  const apiUrl = process.env.REACT_APP_API || 'https://api.vidal-app.com';
                  window.open(`${apiUrl}/movimentacoes/download-pdfs?chaves=${chaves}&projeto=${projeto}&token=${encodeURIComponent(token)}`, '_blank');
                }}
              >
                <FiFileText />
                Baixar PDFs {selectedItems.size > 0 && `(${selectedItems.size})`}
              </button>
              <button
                className="toolbar-btn"
                disabled={selectedItems.size === 0}
                onClick={() => {
                  const chaves = Array.from(selectedItems).join(',');
                  const projeto = localStorage.getItem('vidal_selected_project');
                  const token = JSON.parse(localStorage.getItem('vidal_auth') || '{}').token || '';
                  const apiUrl = process.env.REACT_APP_API || 'https://api.vidal-app.com';
                  window.open(`${apiUrl}/movimentacoes/download-xmls?chaves=${chaves}&projeto=${projeto}&token=${encodeURIComponent(token)}`, '_blank');
                }}
              >
                <BsFileEarmarkCode />
                Baixar XMLs {selectedItems.size > 0 && `(${selectedItems.size})`}
              </button>
            </div>
          </div>

          {/* Toolbar Inferior - Filtros e Busca */}
          <div className="toolbar" style={{ margin: 0, marginBottom: "0.8em" }}>
            <div className="toolbar-left">
              <button
                className="toolbar-btn"
                onClick={() => setFilterSidebarOpen(true)}
              >
                <FiFilter />
                Filtro Relatórios
              </button>
              <button
                className="toolbar-btn"
                onClick={() => {
                  setFiltros({
                    dataInicio: "",
                    dataFim: "",
                    tipoDocumento: "todos",
                    status: "todos",
                    emissao: "todos",
                    cnpj: "",
                  });
                  setSearchText("");
                  // Limpar debounce pendente
                  if (searchDebounceRef.current) {
                    clearTimeout(searchDebounceRef.current);
                  }
                  // Buscar sem filtros
                  fetchMovimentacoes(0, 50, false, {
                    dataInicio: "",
                    dataFim: "",
                    tipoDocumento: "todos",
                    status: "todos",
                    emissao: "todos",
                    cnpj: "",
                  }, "");
                }}
              >
                <FiX />
                Limpar Filtros
              </button>
              <div className="search-box">
                <MdSearch />
                <input
                  type="text"
                  placeholder="Buscar por número, chave, CNPJ ou emitente"
                  value={searchText}
                  onChange={(e) => handleSearchTextChange(e.target.value)}
                />
              </div>
              {(filtros.tipoDocumento !== "todos" || 
                filtros.status !== "todos" || 
                filtros.emissao !== "todos" ||
                filtros.cfop ||
                filtros.emitenteNome ||
                filtros.emitenteCnpj ||
                filtros.destinatarioNome ||
                filtros.destinatarioCnpj ||
                filtros.dataInicio ||
                filtros.dataFim ||
                searchText) && (
                <span className="docs-count">
                  {pagination.total} docs
                </span>
              )}
            </div>
          </div>

          {/* Badges de Filtros Ativos */}
          {(filtros.tipoDocumento !== "todos" ||
            filtros.status !== "todos" ||
            filtros.emissao !== "todos" ||
            filtros.cfop ||
            filtros.emitenteNome ||
            filtros.emitenteCnpj ||
            filtros.destinatarioNome ||
            filtros.destinatarioCnpj ||
            filtros.dataInicio ||
            filtros.dataFim ||
            selectedEmpresas.length > 0) && (
            <div className="active-filters">
              <span className="active-filters-label">
                <FiFilter />
                Filtros ativos:
              </span>
              <div className="active-filters-badges">
                {filtros.tipoDocumento !== "todos" && (
                  <div className="filter-badge-active">
                    <span>Tipo: {filtros.tipoDocumento === "nfe" ? "NFe" : "CTe"}</span>
                    <button onClick={() => removeFilter("tipoDocumento")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {filtros.status !== "todos" && (
                  <div className="filter-badge-active">
                    <span>Status: {filtros.status === "autorizada" ? "Autorizada" : "Cancelada"}</span>
                    <button onClick={() => removeFilter("status")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {filtros.emissao !== "todos" && (
                  <div className="filter-badge-active">
                    <span>Emissão: {filtros.emissao === "propria" ? "Própria" : "Terceiros"}</span>
                    <button onClick={() => removeFilter("emissao")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {filtros.cfop && (
                  <div className="filter-badge-active">
                    <span>CFOP: {filtros.cfop}</span>
                    <button onClick={() => removeFilter("cfop", "")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {filtros.emitenteNome && (
                  <div className="filter-badge-active">
                    <span>Emitente: {filtros.emitenteNome}</span>
                    <button onClick={() => removeFilter("emitenteNome", "")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {filtros.emitenteCnpj && (
                  <div className="filter-badge-active">
                    <span>CNPJ Emit.: {filtros.emitenteCnpj}</span>
                    <button onClick={() => removeFilter("emitenteCnpj", "")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {filtros.destinatarioNome && (
                  <div className="filter-badge-active">
                    <span>Destinatário: {filtros.destinatarioNome}</span>
                    <button onClick={() => removeFilter("destinatarioNome", "")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {filtros.destinatarioCnpj && (
                  <div className="filter-badge-active">
                    <span>CNPJ Dest.: {filtros.destinatarioCnpj}</span>
                    <button onClick={() => removeFilter("destinatarioCnpj", "")}>
                      <FiX />
                    </button>
                  </div>
                )}
                {(filtros.dataInicio || filtros.dataFim) && (
                  <div className="filter-badge-active">
                    <span>
                      Período: {filtros.dataInicio || "..."} até {filtros.dataFim || "..."}
                    </span>
                    <button onClick={() => {
                      const newFiltros = { ...filtros, dataInicio: "", dataFim: "" };
                      setFiltros(newFiltros);
                      fetchMovimentacoes(0, 50, false, newFiltros);
                    }}>
                      <FiX />
                    </button>
                  </div>
                )}
                {selectedEmpresas.map((cnpj) => {
                  const empresa = empresas.find(e => e.cnpj === cnpj);
                  const empresaIndex = empresas.findIndex(e => e.cnpj === cnpj);
                  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
                  const empresaColor = colors[empresaIndex % colors.length];
                  
                  if (!empresa) return null;
                  
                  return (
                    <div key={cnpj} className="filter-badge-active empresas-filter">
                      <div 
                        className="empresa-thumb"
                        style={{ 
                          background: `${empresaColor}20`,
                          borderColor: empresaColor
                        }}
                      >
                        <BiBuildings style={{ color: empresaColor }} />
                      </div>
                      <span>
                        {empresa.nome} - {formatarCNPJ(cnpj)}
                      </span>
                      <button onClick={() => {
                        setSelectedEmpresas(prev => prev.filter(c => c !== cnpj));
                      }}>
                        <FiX />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {console.log('[Render] movimentacoes:', movimentacoes.length, 'sefazStatus:', sefazStatus, 'empresas:', empresas.length)}
          {movimentacoes.length === 0 ? (
            empresas.length === 0 ? (
              <EmptyState screen="certificates" compact />
            ) : sefazStatus.captureEnabled ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center', minHeight: 'calc(100vh - 220px)' }}>
                <div style={{ width: 72, height: 72, borderRadius: 20, background: `${theme.primary || '#3b82f6'}15`, border: `1px solid ${theme.primary || '#3b82f6'}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <FiActivity style={{ fontSize: 32, color: theme.primary || '#3b82f6', opacity: 0.85 }} />
                </div>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: theme.text, margin: '0 0 10px' }}>Captura em andamento</h2>
                <p style={{ fontSize: '0.85rem', color: theme.text, opacity: 0.6, maxWidth: 400, lineHeight: 1.6, margin: '0 0 24px' }}>
                  A captura automática de notas fiscais está ativada. As primeiras movimentações devem aparecer em breve (até 90 dias retroativos). Aguarde alguns minutos.
                </p>
              </div>
            ) : sefazStatus.canSelfActivate ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center', minHeight: 'calc(100vh - 220px)' }}>
                <div style={{ width: 72, height: 72, borderRadius: 20, background: `${theme.primary || '#3b82f6'}15`, border: `1px solid ${theme.primary || '#3b82f6'}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <FiShield style={{ fontSize: 32, color: theme.primary || '#3b82f6', opacity: 0.85 }} />
                </div>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: theme.text, margin: '0 0 10px' }}>Nenhuma movimentação disponível</h2>
                <p style={{ fontSize: '0.85rem', color: theme.text, opacity: 0.6, maxWidth: 400, lineHeight: 1.6, margin: '0 0 24px' }}>
                  Certificados encontrados, mas a captura automática SEFAZ ainda não foi ativada. Clique abaixo para iniciar a captura de notas fiscais.
                </p>
                <button
                  onClick={() => setShowSefazModal(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, background: theme.primary || '#3b82f6', color: '#fff', fontWeight: 500, fontSize: '0.85rem', border: 'none', cursor: 'pointer' }}
                >
                  <FiShield /> Ativar Captura SEFAZ
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center', minHeight: 'calc(100vh - 220px)' }}>
                <div style={{ width: 72, height: 72, borderRadius: 20, background: `${theme.warning || '#f59e0b'}15`, border: `1px solid ${theme.warning || '#f59e0b'}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
                  <FiAlertCircle style={{ fontSize: 32, color: theme.warning || '#f59e0b', opacity: 0.85 }} />
                </div>
                <h2 style={{ fontSize: '1.15rem', fontWeight: 600, color: theme.text, margin: '0 0 10px' }}>Nenhuma movimentação disponível</h2>
                <p style={{ fontSize: '0.85rem', color: theme.text, opacity: 0.6, maxWidth: 400, lineHeight: 1.6, margin: '0 0 24px' }}>
                  Certificados encontrados, mas a captura automática SEFAZ ainda não foi ativada. Entre em contato com um administrador do sistema para ativar a captura de notas fiscais.
                </p>
              </div>
            )
          ) : (
            <>
              {/* Tabela de NFes - Flex */}
              <div className="table-container">
                <div className="table-header">
                  <div className="header-cell col-dot"></div>
                  <div className="header-cell col-data sortable" onClick={() => handleSort("data")}>
                    <FiCalendar /> Data {renderSortIcon("data")}
                  </div>
                  <div className="header-cell col-chave sortable" onClick={() => handleSort("chave")}>
                    <FiKey /> Chave {renderSortIcon("chave")}
                  </div>
                  <div className="header-cell col-status sortable" onClick={() => handleSort("status")}>
                    <FiCheckCircle /> Status {renderSortIcon("status")}
                  </div>
                  <div className="header-cell col-numero sortable" onClick={() => handleSort("numero")}>
                    <FiHash /> Número NFE {renderSortIcon("numero")}
                  </div>
                  <div className="header-cell col-emitente-info sortable" onClick={() => handleSort("emitente")}>
                    <BiBuildings /> Emitente {renderSortIcon("emitente")}
                  </div>
                  <div className="header-cell col-destinatario-info sortable" onClick={() => handleSort("destinatario")}>
                    <HiOutlineUserGroup /> Destinatário {renderSortIcon("destinatario")}
                  </div>
                  <div className="header-cell col-cfop sortable" onClick={() => handleSort("cfop")}>
                    <FiHash /> CFOP {renderSortIcon("cfop")}
                  </div>
                  <div className="header-cell col-actions"></div>
                </div>
                <div className="table-body" style={{ opacity: refreshing ? 0.5 : 1 }}>
                  {loading ? (
                    <div className="loading-state">
                      <p>Carregando movimentações...</p>
                    </div>
                  ) : (
                <>
                  {filteredMovimentacoes.map((mov, index) => {
                    const isSelected = selectedItems.has(mov.chave);
                    return (
                      <div
                        className={`table-row ${isSelected ? 'selected' : ''}`}
                        key={mov.id}
                        onClick={(e) => handleItemClick(mov, index, e)}
                        style={{ 
                          cursor: "pointer", 
                          position: "relative",
                          background: isSelected ? 'rgba(59, 130, 246, 0.1)' : undefined,
                          borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent',
                          userSelect: selectionMode ? 'none' : undefined
                        }}
                      >
                        {selectionMode && (
                          <div className="cell" style={{ width: '40px', minWidth: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isSelected ? (
                              <FiCheckSquare style={{ color: '#3b82f6', fontSize: '18px' }} />
                            ) : (
                              <FiSquare style={{ color: '#888', fontSize: '18px' }} />
                            )}
                          </div>
                        )}
                        <div className="cell col-dot">
                          <span
                            className={`dot ${
                              mov.modelo === "57" ? "blue" : "green"
                            }`}
                          ></span>
                          {!mov.hasReformFields && (
                            <FiAlertCircle 
                              style={{ 
                                color: '#f59e0b', 
                                fontSize: '16px',
                                marginLeft: '6px'
                              }}
                              title="Documento sem campos da Reforma Tributária"
                            />
                          )}
                        </div>
                        <div className="cell col-data">
                          {mov.data
                            ? new Date(mov.data).toLocaleDateString("pt-BR")
                            : "N/A"}
                        </div>
                        <div className="cell col-chave">
                          {mov.chave || "N/A"}
                        </div>
                        <div className="cell col-status">
                          <span
                            className={`status-badge ${
                              mov.status === "Autorizada"
                                ? "authorized"
                                : "cancelled"
                            }`}
                          >
                            {mov.status || "N/A"}
                          </span>
                        </div>
                        <div className="cell col-numero">
                          {mov.numero || "N/A"}
                        </div>
                        <div className="cell col-emitente-info">
                          <div className="stacked-info">
                            <span className="info-name">{mov.emiNome || mov.emitente || "N/A"}</span>
                            <span className="info-cnpj">{mov.emiDoc ? formatarCNPJ(mov.emiDoc) : "N/A"}</span>
                          </div>
                        </div>
                        <div className="cell col-destinatario-info">
                          <div className="stacked-info">
                            <span className="info-name">{mov.destNome || mov.destinatario || "N/A"}</span>
                            <span className="info-cnpj">{mov.destDoc ? formatarCNPJ(mov.destDoc) : "N/A"}</span>
                          </div>
                        </div>
                        <div className="cell col-cfop">
                          {mov.cfop || "N/A"}
                        </div>
                        <button
                          className="action-btn"
                          onClick={(e) => handleDownloadXml(mov.chave, e)}
                          title="Baixar XML"
                          onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = "0.5"}
                          style={{ 
                            position: "absolute", 
                            right: 0, 
                            marginRight: "2em",
                            background: "transparent",
                            border: "none",
                            opacity: "0.5",
                            cursor: "pointer",
                            fontSize: "18px",
                            transition: "opacity 0.2s ease"
                          }}
                        >
                          <FiDownload />
                        </button>
                      </div>
                    );
                  })}
                  {!searchText && pagination.hasMore && (
                    <div
                      ref={observerTarget}
                      style={{ height: "20px", margin: "10px 0" }}
                    >
                      {loadingMore && (
                        <p style={{ textAlign: "center", opacity: 0.6 }}>
                          Carregando mais...
                        </p>
                      )}
                    </div>
                  )}
                  {refreshing && (
                    <div className="refreshing-overlay">
                      <FiLoader />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
        </div>
      )}

      {/* ========== TAB MENSAL - REMOVIDO ========== */}
      {false && (
        <div className="mensal-layout">
          {/* Header com navegação de mês e cards de métricas */}
          <div className="mensal-top-bar">
            <div className="month-selector">
              <button
                className="month-btn prev"
                onClick={() => navegarMes("anterior")}
              >
                <MdChevronLeft />
              </button>
              <div className="month-display">
                <FiCalendar className="month-icon" />
                <span className="month-text">{obterNomeMes()}</span>
              </div>
              <button
                className="month-btn next"
                onClick={() => navegarMes("proximo")}
              >
                <MdChevronRight />
              </button>
            </div>

            <div className="metric-cards">
              <div className="metric-card blue">
                <div className="metric-icon blue">
                  <FiFileText />
                </div>
                <div className="metric-content">
                  <span className="metric-value">
                    {formatNumber(dadosMensal.monthlyNfe)}
                  </span>
                  <span className="metric-label">NFe</span>
                </div>
              </div>
              <div className="metric-card green">
                <div className="metric-icon green">
                  <HiOutlineDocumentText />
                </div>
                <div className="metric-content">
                  <span className="metric-value">
                    {formatNumber(dadosMensal.monthlyCte)}
                  </span>
                  <span className="metric-label">CTe</span>
                </div>
              </div>
              <div className="metric-card purple">
                <div className="metric-icon purple">
                  <BiBuildings />
                </div>
                <div className="metric-content">
                  <span className="metric-value">
                    {formatNumber(dadosMensal.monthlyPropria)}
                  </span>
                  <span className="metric-label">Própria</span>
                </div>
              </div>
              <div className="metric-card orange">
                <div className="metric-icon orange">
                  <FiUser />
                </div>
                <div className="metric-content">
                  <span className="metric-value">
                    {formatNumber(dadosMensal.monthlyTerceiros)}
                  </span>
                  <span className="metric-label">Terceiros</span>
                </div>
              </div>
              <div className="metric-card red">
                <div className="metric-icon red">
                  <FiXCircle />
                </div>
                <div className="metric-content">
                  <span className="metric-value">
                    {dadosMensal.monthlyCanceladas}
                  </span>
                  <span className="metric-label">Canceladas</span>
                </div>
              </div>
            </div>
          </div>

          {/* Layout Principal: Tabelas + Gráficos */}
          <div className="mensal-main">
            {/* Grid das Tabelas */}
            <div className="tables-row">
              {/* Tabela por Dia */}
              <div className="table-container compact">
                <div className="table-header">
                  <div className="header-icon">
                    <HiOutlineDocumentText />
                  </div>
                  <span>Estatísticas por Dia</span>
                </div>
                <div className="table-inner">
                  <div className="table-row header">
                    <div className="header-cell col-data">Dia</div>
                    <div className="header-cell">NFe</div>
                    <div className="header-cell">CTe</div>
                    <div className="header-cell">Própria</div>
                    <div className="header-cell">Terc.</div>
                    <div className="header-cell">Canc.</div>
                    <div className="header-cell">Total</div>
                  </div>
                  <div className="table-body">
                    {Object.keys(dadosMensal.reportsByDay)
                      .filter((day) => {
                        const d = dadosMensal.reportsByDay[day];
                        return d.nfe > 0 || d.cte > 0;
                      })
                      .map((day) => {
                        const dayData = dadosMensal.reportsByDay[day];
                        const [, mes] = mesAno.split("-");
                        const total = dayData.nfe + dayData.cte;
                        return (
                          <div className="table-row" key={day}>
                            <div className="cell col-data">
                              {String(day).padStart(2, "0")}/{mes}
                            </div>
                            <div className="cell">{dayData.nfe}</div>
                            <div className="cell">{dayData.cte}</div>
                            <div className="cell">{dayData.propria}</div>
                            <div className="cell">{dayData.terceiros}</div>
                            <div className="cell">{dayData.canceladas}</div>
                            <div className="cell">{total}</div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>

              {/* Tabela por Mês */}
              <div className="table-container compact">
                <div className="table-header">
                  <div className="header-icon">
                    <BiSpreadsheet />
                  </div>
                  <span>Estatísticas por Mês</span>
                </div>
                <div className="table-inner">
                  <div className="table-row header">
                    <div className="header-cell col-data">Mês</div>
                    <div className="header-cell">NFe</div>
                    <div className="header-cell">CTe</div>
                    <div className="header-cell">Própria</div>
                    <div className="header-cell">Terc.</div>
                    <div className="header-cell">Canc.</div>
                    <div className="header-cell">Total</div>
                  </div>
                  <div className="table-body">
                    {dadosMeses.map((mesData, index) => (
                      <div className="table-row" key={index}>
                        <div className="cell col-data">{mesData.mes}</div>
                        <div className="cell">{formatNumber(mesData.nfe)}</div>
                        <div className="cell">{formatNumber(mesData.cte)}</div>
                        <div className="cell">
                          {formatNumber(mesData.propria)}
                        </div>
                        <div className="cell">
                          {formatNumber(mesData.terceiros)}
                        </div>
                        <div className="cell">
                          {formatNumber(mesData.canceladas)}
                        </div>
                        <div className="cell">
                          {formatNumber(mesData.nfe + mesData.cte)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Coluna dos Gráficos Donut */}
            <div className="charts-column">
              {/* Gráfico por Tipo */}
              <div className="chart-card">
                <div className="chart-header">
                  <span className="chart-title">Por Tipo</span>
                </div>
                <div className="chart-content">
                  <ResponsiveContainer width="100%" height={120}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: "NFe", value: dadosMensal.monthlyNfe },
                          { name: "CTe", value: dadosMensal.monthlyCte },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={50}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                      >
                        <Cell fill="#3b82f6" />
                        <Cell fill="#10b981" />
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "rgba(15, 18, 22, 0.95)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "8px",
                          fontSize: "12px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                          color: "#ffffff",
                        }}
                        itemStyle={{ color: "#ffffff" }}
                        formatter={(value, name) => [
                          value.toLocaleString("pt-BR"),
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <span
                      className="legend-dot"
                      style={{ background: "#3b82f6" }}
                    ></span>
                    <span className="legend-label">NFe</span>
                    <span className="legend-value">
                      {formatNumber(dadosMensal.monthlyNfe)}
                    </span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="legend-dot"
                      style={{ background: "#10b981" }}
                    ></span>
                    <span className="legend-label">CTe</span>
                    <span className="legend-value">
                      {formatNumber(dadosMensal.monthlyCte)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Gráfico por Origem */}
              <div className="chart-card">
                <div className="chart-header">
                  <span className="chart-title">Por Origem</span>
                </div>
                <div className="chart-content">
                  <ResponsiveContainer width="100%" height={120}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Própria",
                            value: dadosMensal.monthlyPropria,
                          },
                          {
                            name: "Terceiros",
                            value: dadosMensal.monthlyTerceiros,
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={50}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                      >
                        <Cell fill="#8b5cf6" />
                        <Cell fill="#f59e0b" />
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "rgba(15, 18, 22, 0.95)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "8px",
                          fontSize: "12px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                          color: "#ffffff",
                        }}
                        itemStyle={{ color: "#ffffff" }}
                        formatter={(value, name) => [
                          value.toLocaleString("pt-BR"),
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <span
                      className="legend-dot"
                      style={{ background: "#8b5cf6" }}
                    ></span>
                    <span className="legend-label">Própria</span>
                    <span className="legend-value">
                      {formatNumber(dadosMensal.monthlyPropria)}
                    </span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="legend-dot"
                      style={{ background: "#f59e0b" }}
                    ></span>
                    <span className="legend-label">Terceiros</span>
                    <span className="legend-value">
                      {formatNumber(dadosMensal.monthlyTerceiros)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Gráfico por Status */}
              <div className="chart-card">
                <div className="chart-header">
                  <span className="chart-title">Por Status</span>
                </div>
                <div className="chart-content">
                  <ResponsiveContainer width="100%" height={120}>
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: "Autorizadas",
                            value:
                              dadosMensal.monthlyNfe +
                              dadosMensal.monthlyCte -
                              dadosMensal.monthlyCanceladas,
                          },
                          {
                            name: "Canceladas",
                            value: dadosMensal.monthlyCanceladas,
                          },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={30}
                        outerRadius={50}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                      >
                        <Cell fill="#10b981" />
                        <Cell fill="#ef4444" />
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "rgba(15, 18, 22, 0.95)",
                          border: "1px solid rgba(255,255,255,0.08)",
                          borderRadius: "8px",
                          fontSize: "12px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                          color: "#ffffff",
                        }}
                        itemStyle={{ color: "#ffffff" }}
                        formatter={(value, name) => [
                          value.toLocaleString("pt-BR"),
                          name,
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="chart-legend">
                  <div className="legend-item">
                    <span
                      className="legend-dot"
                      style={{ background: "#10b981" }}
                    ></span>
                    <span className="legend-label">Autorizadas</span>
                    <span className="legend-value">
                      {formatNumber(
                        dadosMensal.monthlyNfe +
                          dadosMensal.monthlyCte -
                          dadosMensal.monthlyCanceladas
                      )}
                    </span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="legend-dot"
                      style={{ background: "#ef4444" }}
                    ></span>
                    <span className="legend-label">Canceladas</span>
                    <span className="legend-value">
                      {formatNumber(dadosMensal.monthlyCanceladas)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de Barras - Evolução Diária NFe vs CTe */}
          <div className="chart-section">
            <div className="chart-header">
              <span className="chart-title">Evolução Diária</span>
              <div className="chart-legend">
                <span className="legend-item">
                  <span
                    className="dot"
                    style={{ background: "#3b82f6" }}
                  ></span>
                  NFe
                </span>
                <span className="legend-item">
                  <span
                    className="dot"
                    style={{ background: "#10b981" }}
                  ></span>
                  CTe
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={Object.keys(dadosMensal.reportsByDay)
                  .filter((day) => {
                    const d = dadosMensal.reportsByDay[day];
                    return d.nfe > 0 || d.cte > 0;
                  })
                  .map((day) => {
                    const [, mes] = mesAno.split("-");
                    return {
                      dia: `${String(day).padStart(2, "0")}/${mes}`,
                      nfe: dadosMensal.reportsByDay[day].nfe,
                      cte: dadosMensal.reportsByDay[day].cte,
                    };
                  })}
                margin={{ top: 10, right: 15, left: 5, bottom: 25 }}
                barGap={1}
                barCategoryGap="18%"
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={theme.border}
                  vertical={false}
                />
                <XAxis
                  dataKey="dia"
                  tick={{ fontSize: 10, fill: theme.text }}
                  axisLine={{ stroke: theme.border }}
                  tickLine={false}
                  dy={8}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: theme.text }}
                  axisLine={{ stroke: theme.border }}
                  tickLine={false}
                  width={40}
                  tickFormatter={(value) => {
                    if (value === 0) return "0";
                    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
                    return value;
                  }}
                  tickCount={5}
                />
                <Tooltip
                  contentStyle={{
                    background: "rgba(15, 18, 22, 0.95)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    fontSize: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                    color: "#ffffff",
                  }}
                  itemStyle={{ color: "#ffffff" }}
                  labelStyle={{ color: "#ffffff" }}
                  formatter={(value, name) => [
                    value.toLocaleString("pt-BR"),
                    name,
                  ]}
                  labelFormatter={(label) => `Dia ${label}`}
                  cursor={{ fill: "rgba(255,255,255,0.03)" }}
                />
                <Bar
                  dataKey="nfe"
                  name="NFe"
                  fill="#3b82f6"
                  radius={[3, 3, 0, 0]}
                />
                <Bar
                  dataKey="cte"
                  name="CTe"
                  fill="#10b981"
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Menu Lateral de Filtros */}
      <div className={`filter-sidebar ${filterSidebarOpen ? "open" : ""}`}>
        <div className="filter-sidebar-header">
          <h4>
            <FiFilter />
            Filtros
          </h4>
          <button
            className="close-btn"
            onClick={() => setFilterSidebarOpen(false)}
          >
            <MdClose />
          </button>
        </div>

        <div className="filter-sidebar-content">
          {/* Período */}
          <div className="filter-group">
            <label className="filter-label">
              <FiCalendar />
              Período
            </label>
            <div className="date-range">
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) =>
                  setFiltros({ ...filtros, dataInicio: e.target.value })
                }
                placeholder="Data início"
              />
              <span>até</span>
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) =>
                  setFiltros({ ...filtros, dataFim: e.target.value })
                }
                placeholder="Data fim"
              />
            </div>
          </div>

          {/* Tipo de Documento */}
          <div className="filter-group">
            <label className="filter-label">
              <HiOutlineDocumentText />
              Tipo de Documento
            </label>
            <div className="filter-badges">
              <button
                className={`filter-badge ${filtros.tipoDocumento === "todos" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, tipoDocumento: "todos" })}
              >
                Todos
              </button>
              <button
                className={`filter-badge ${filtros.tipoDocumento === "cte" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, tipoDocumento: "cte" })}
              >
                CTe
              </button>
              <button
                className={`filter-badge ${filtros.tipoDocumento === "nfe" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, tipoDocumento: "nfe" })}
              >
                NFe
              </button>
            </div>
          </div>

          {/* Status */}
          <div className="filter-group">
            <label className="filter-label">
              <FiCheckCircle />
              Status
            </label>
            <div className="filter-badges">
              <button
                className={`filter-badge ${filtros.status === "todos" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, status: "todos" })}
              >
                Todos
              </button>
              <button
                className={`filter-badge ${filtros.status === "autorizada" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, status: "autorizada" })}
              >
                Autorizada
              </button>
              <button
                className={`filter-badge ${filtros.status === "cancelada" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, status: "cancelada" })}
              >
                Cancelada
              </button>
            </div>
          </div>

          {/* CFOP */}
          <div className="filter-group">
            <label className="filter-label">
              <FiHash />
              CFOP
            </label>
            <input
              type="text"
              value={filtros.cfop || ""}
              onChange={(e) => handleCfopChange(e.target.value)}
              placeholder="Ex: 5102, 6102 ou digite descrição"
            />
            {showCfopSuggestions && (
              <div className="emitente-suggestions">
                {loadingCfops ? (
                  <div className="suggestion-item loading">Buscando...</div>
                ) : (
                  cfopSuggestions.map((cfop, index) => (
                    <div
                      key={index}
                      className="suggestion-item cfop-item"
                      onClick={() => selectCfop(cfop)}
                    >
                      <div className="suggestion-icon">
                        <FiHash />
                      </div>
                      <div className="suggestion-info">
                        <span className="suggestion-name">{cfop.codigo}</span>
                        <span className="suggestion-cnpj">{cfop.descricao}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Empresas (Matriz e Filiais) */}
          <div className="filter-section">
            <div className="filter-section-header">
              <BiBuildings />
              <span>Empresas</span>
              {selectedEmpresas.length > 0 && (
                <span className="selected-count">
                  {selectedEmpresas.length} selecionada{selectedEmpresas.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="filter-section-content">
              <div className="empresas-container">
                {loadingEmpresas ? (
                  <div className="empresas-loading">
                    <p>Carregando empresas...</p>
                  </div>
                ) : empresas.length === 0 ? (
                  <div className="empresas-empty">
                    <BiBuildings />
                    <p>Nenhuma empresa encontrada</p>
                  </div>
                ) : (
                  <>
                    <div className="empresas-list">
                      {empresas.map((empresa, index) => {
                        const isSelected = selectedEmpresas.includes(empresa.cnpj);
                        // Gerar cor única para cada empresa baseada no índice
                        const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
                        const empresaColor = colors[index % colors.length];
                        
                        return (
                          <div
                            key={empresa.cnpj}
                            className={`empresa-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleEmpresa(empresa.cnpj)}
                          >
                            <div className="empresa-icon" style={{ 
                              background: `${empresaColor}15`,
                              borderColor: `${empresaColor}40`
                            }}>
                              <BiBuildings style={{ color: empresaColor }} />
                            </div>
                            <div className="empresa-info">
                              <span className="empresa-nome">
                                {empresa.nome || 'Empresa sem nome'}
                              </span>
                              <span className="empresa-cnpj">
                                CNPJ: {formatarCNPJ(empresa.cnpj)}
                              </span>
                            </div>
                            {isSelected && (
                              <div className="empresa-check">
                                <FiCheckCircle />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Emissão */}
          <div className="filter-group">
            <label className="filter-label">
              <BiBuildings />
              Emissão
            </label>
            <div className="filter-badges">
              <button
                className={`filter-badge ${filtros.emissao === "todos" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, emissao: "todos" })}
              >
                Todos
              </button>
              <button
                className={`filter-badge ${filtros.emissao === "propria" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, emissao: "propria" })}
              >
                Própria
              </button>
              <button
                className={`filter-badge ${filtros.emissao === "terceiros" ? "active" : ""}`}
                onClick={() => setFiltros({ ...filtros, emissao: "terceiros" })}
              >
                Terceiros
              </button>
            </div>
          </div>

          {/* Emitente */}
          <div className="filter-section">
            <div className="filter-section-header">
              <BiBuildings />
              <span>Emitente</span>
            </div>
            <div className="filter-section-content">
              <div className="filter-group">
                <label className="filter-label">
                  <HiOutlineOfficeBuilding />
                  Nome
                </label>
                <input
                  type="text"
                  value={filtros.emitenteNome || ""}
                  onChange={(e) => handleEmitenteChange(e.target.value)}
                  placeholder="Nome do emitente"
                />
                {showEmitenteSuggestions && emitenteActiveField === 'nome' && (
                  <div className="emitente-suggestions">
                    {loadingEmitentes ? (
                      <div className="suggestion-item loading">Buscando...</div>
                    ) : (
                      emitenteSuggestions.map((emitente, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => selectEmitente(emitente)}
                        >
                          <div className="suggestion-icon">
                            <BiBuildings />
                          </div>
                          <div className="suggestion-info">
                            <span className="suggestion-name">{emitente.nome}</span>
                            <span className="suggestion-cnpj">{emitente.cnpj}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="filter-group">
                <label className="filter-label">
                  <HiOutlineIdentification />
                  CNPJ
                </label>
                <input
                  type="text"
                  value={filtros.emitenteCnpj || ""}
                  onChange={(e) => handleEmitenteCnpjChange(e.target.value)}
                  placeholder="00.000.000/0000-00"
                />
                {showEmitenteSuggestions && emitenteActiveField === 'cnpj' && (
                  <div className="emitente-suggestions">
                    {loadingEmitentes ? (
                      <div className="suggestion-item loading">Buscando...</div>
                    ) : (
                      emitenteSuggestions.map((emitente, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => selectEmitente(emitente)}
                        >
                          <div className="suggestion-icon">
                            <BiBuildings />
                          </div>
                          <div className="suggestion-info">
                            <span className="suggestion-name">{emitente.nome}</span>
                            <span className="suggestion-cnpj">{emitente.cnpj}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Destinatário */}
          <div className="filter-section">
            <div className="filter-section-header">
              <HiOutlineUserGroup />
              <span>Destinatário</span>
            </div>
            <div className="filter-section-content">
              <div className="filter-group">
                <label className="filter-label">
                  <HiOutlineOfficeBuilding />
                  Nome
                </label>
                <input
                  type="text"
                  value={filtros.destinatarioNome || ""}
                  onChange={(e) => handleDestinatarioChange(e.target.value)}
                  placeholder="Nome do destinatário"
                />
                {showDestinatarioSuggestions && destinatarioActiveField === 'nome' && (
                  <div className="emitente-suggestions">
                    {loadingDestinatarios ? (
                      <div className="suggestion-item loading">Buscando...</div>
                    ) : (
                      destinatarioSuggestions.map((destinatario, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => selectDestinatario(destinatario)}
                        >
                          <div className="suggestion-icon">
                            <BiBuildings />
                          </div>
                          <div className="suggestion-info">
                            <span className="suggestion-name">{destinatario.nome}</span>
                            <span className="suggestion-cnpj">{destinatario.cnpj}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <div className="filter-group">
                <label className="filter-label">
                  <HiOutlineIdentification />
                  CNPJ
                </label>
                <input
                  type="text"
                  value={filtros.destinatarioCnpj || ""}
                  onChange={(e) => handleDestinatarioCnpjChange(e.target.value)}
                  placeholder="00.000.000/0000-00"
                />
                {showDestinatarioSuggestions && destinatarioActiveField === 'cnpj' && (
                  <div className="emitente-suggestions">
                    {loadingDestinatarios ? (
                      <div className="suggestion-item loading">Buscando...</div>
                    ) : (
                      destinatarioSuggestions.map((destinatario, index) => (
                        <div
                          key={index}
                          className="suggestion-item"
                          onClick={() => selectDestinatario(destinatario)}
                        >
                          <div className="suggestion-icon">
                            <BiBuildings />
                          </div>
                          <div className="suggestion-info">
                            <span className="suggestion-name">{destinatario.nome}</span>
                            <span className="suggestion-cnpj">{destinatario.cnpj}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="filter-sidebar-footer">
          <button
            className="btn-clear"
            onClick={() => {
              setFiltros({
                dataInicio: "",
                dataFim: "",
                tipoDocumento: "todos",
                status: "todos",
                emissao: "todos",
                cfop: "",
                emitenteNome: "",
                emitenteCnpj: "",
                destinatarioNome: "",
                destinatarioCnpj: "",
              });
              setSelectedEmpresas([]);
              fetchMovimentacoes(0, 50, false);
            }}
          >
            <FiX />
            Limpar
          </button>
          <button
            className="btn-apply"
            onClick={() => {
              fetchMovimentacoes(0, 50, false);
              setFilterSidebarOpen(false);
            }}
          >
            <FiCheckCircle />
            Aplicar
          </button>
        </div>
      </div>

      {/* Overlay */}
      {filterSidebarOpen && (
        <div
          className="filter-overlay"
          onClick={() => setFilterSidebarOpen(false)}
        />
      )}

      {/* Modal Ativar Captura SEFAZ */}
      {showSefazModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)',
        }} onClick={() => { if (!activatingSefaz) { setShowSefazModal(false); setSefazPassword(''); } }}>
          <div style={{
            background: theme.card || '#fff',
            border: `1px solid ${theme.border || '#e5e7eb'}`,
            borderRadius: 12, padding: 32, width: 420, maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${theme.primary || '#3b82f6'}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiShield style={{ color: theme.primary || '#3b82f6', fontSize: 20 }} />
              </div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: theme.text }}>Ativar Captura SEFAZ</h3>
            </div>
            <p style={{ margin: '0 0 20px', fontSize: '0.85rem', color: theme.text, opacity: 0.7, lineHeight: 1.5 }}>
              Digite a senha do certificado para confirmar a ativação da captura automática de notas fiscais. A senha será usada para configurar o serviço SEFAZ.
            </p>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 500, color: theme.text, opacity: 0.8, marginBottom: 6 }}>Senha do Certificado</label>
              <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${theme.border || '#e5e7eb'}`, borderRadius: 8, padding: '0 12px', background: theme.card2 || theme.body || '#f9fafb' }}>
                <FiLock style={{ color: theme.text, opacity: 0.5, fontSize: 16, marginRight: 8 }} />
                <input
                  type="password"
                  value={sefazPassword}
                  onChange={e => setSefazPassword(e.target.value)}
                  placeholder="Digite a senha do certificado .pfx"
                  style={{ border: 'none', background: 'transparent', outline: 'none', flex: 1, padding: '10px 0', fontSize: '0.9rem', color: theme.text }}
                  onKeyDown={e => { if (e.key === 'Enter') handleActivateSefaz(); }}
                  autoFocus
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowSefazModal(false); setSefazPassword(''); }}
                disabled={activatingSefaz}
                style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${theme.border || '#e5e7eb'}`, background: theme.card || '#fff', color: theme.text, fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleActivateSefaz}
                disabled={!sefazPassword || activatingSefaz}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: theme.primary || '#3b82f6', color: '#fff', fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem', opacity: (!sefazPassword || activatingSefaz) ? 0.6 : 1 }}
              >
                {activatingSefaz ? 'Ativando...' : 'Ativar Captura'}
              </button>
            </div>
          </div>
        </div>
      )}

    </Container>
  );
}
